import type {ConnectionOperation} from '@github-ui/optimistic-connection'
import type {MilestoneState} from '@github-ui/server-persisted-queries/types/RepositoryMilestoneIndexPageQuery'

/**
 * Declarative connection ops for the Data Router milestone index. The index shows one state at a time, so
 * close/reopen and delete remove the row from the current list and shift both tab counts. Counts render
 * from this full-list consumer (the engine can't dedupe count-only documents). Settle folds the current
 * view only — other tabs/pages and the navigate-away create/edit reconcile via the route's nav SWR.
 */

/** Owner-relative path to the milestones connection. */
export const MILESTONES_CONNECTION_PATH = ['milestones']

const OPEN_COUNT_PATH = ['open', 'totalCount']
const CLOSED_COUNT_PATH = ['closed', 'totalCount']
// The current (Open/Closed) list's own total, which drives pagination.
const LIST_COUNT_PATH = [...MILESTONES_CONNECTION_PATH, 'totalCount']

const countPathForState = (state: MilestoneState) => (state === 'CLOSED' ? CLOSED_COUNT_PATH : OPEN_COUNT_PATH)

/** Optimistic close/reopen: the row leaves the current list and both tab counts shift (`fromState` = state before the toggle). */
export function buildToggleMilestoneOperation(id: string, fromState: MilestoneState): ConnectionOperation {
  const toState: MilestoneState = fromState === 'CLOSED' ? 'OPEN' : 'CLOSED'
  return {
    type: 'removeFromConnection',
    connectionPath: MILESTONES_CONNECTION_PATH,
    nodeId: id,
    counts: [
      {path: countPathForState(fromState), delta: -1},
      {path: countPathForState(toState), delta: 1},
      {path: LIST_COUNT_PATH, delta: -1},
    ],
  }
}

/** Confirmed delete: remove the milestone from the current list (−1 its state tab, −1 list total). */
export function buildDeleteMilestoneOperation(id: string, state: MilestoneState): ConnectionOperation {
  return {
    type: 'removeFromConnection',
    connectionPath: MILESTONES_CONNECTION_PATH,
    nodeId: id,
    counts: [
      {path: countPathForState(state), delta: -1},
      {path: LIST_COUNT_PATH, delta: -1},
    ],
  }
}
